/**
 * subscriptionService — HTTP client for /subscriptions/* endpoints.
 *
 * REPLACES the legacy class-based service in this same file. Key
 * differences from the legacy implementation:
 *   - cancelSubscription now POSTs /subscriptions/:id/cancel (the
 *     proper lifecycle endpoint that transitions through 'cancelling'
 *     before 'cancelled'). The legacy code did PUT /subscriptions/:id
 *     {status:'cancelled'} which skipped the cancelling state entirely.
 *   - getUsageAnalytics calls /subscriptions/usage (not /usage-analytics
 *     which doesn't exist on the backend).
 *   - Adds renew and reactivate endpoints that exist on the backend
 *     but had no client wrapper.
 *   - Drops the redirect-only handlePaymentCallback wrapper — that
 *     endpoint is consumed by Paystack, not by the frontend.
 *   - Types come from @/types/subscription.types — comprehensive and
 *     matched to the backend Mongoose schemas.
 *
 * Response shape: every authed endpoint returns { success, data }; the
 * service unwraps to bare typed data. Callers don't see the envelope.
 */
import api from "./api";
import type {
  Subscription,
  SubscriptionPlan,
  BillingHistoryResponse,
  UsageAnalytics,
  UpgradeRequest,
  UpgradeResponse,
  PaymentMethodUpdate,
  ApiEnvelope,
} from "@/types/subscription.types";

// ─── Service ──────────────────────────────────────────────────────

export const subscriptionService = {
  /**
   * Get the current company's subscription. Returns the most-recent
   * subscription in {active, pending, cancelling, grace_period} status
   * (the backend filters out terminal statuses). Throws 404 if none.
   */
  getCurrent: async (): Promise<Subscription> => {
    const res = await api.get<ApiEnvelope<Subscription>>("/subscriptions");
    return res.data.data;
  },

  /**
   * Get a specific subscription by ID. Multi-tenant scoped at the
   * backend (companyId enforced).
   */
  getById: async (id: string): Promise<Subscription> => {
    const res = await api.get<ApiEnvelope<Subscription>>(
      `/subscriptions/${id}`,
    );
    return res.data.data;
  },

  /**
   * List all available subscription plans. Backend returns isActive:true
   * plans sorted by price ascending.
   */
  getPlans: async (): Promise<SubscriptionPlan[]> => {
    const res = await api.get<ApiEnvelope<SubscriptionPlan[]>>(
      "/subscriptions/plans",
    );
    return res.data.data;
  },

  /**
   * Upgrade to a higher-tier plan. Backend computes prorated amount
   * for the remaining days of the current period. Returns either:
   *   - paymentRequired: true with authorizationUrl → redirect user
   *   - paymentRequired: false → upgrade applied immediately
   */
  upgrade: async (request: UpgradeRequest): Promise<UpgradeResponse> => {
    const res = await api.post<ApiEnvelope<UpgradeResponse>>(
      "/subscriptions/upgrade",
      request,
    );
    return res.data.data;
  },

  /**
   * Cancel a subscription. Transitions to 'cancelling' state — service
   * remains usable until endDate, at which point a scheduled job moves
   * it to 'cancelled'. The user can re-activate before endDate.
   *
   * NOTE: backend POST /subscriptions/:id/cancel handles the full
   * lifecycle correctly. Do NOT use PUT /:id {status:'cancelled'} —
   * that bypasses the cancelling state and the cancellation effective
   * date logic.
   */
  cancel: async (
    id: string,
    details?: { reason?: string; feedback?: string },
  ): Promise<Subscription> => {
    const res = await api.post<ApiEnvelope<Subscription>>(
      `/subscriptions/${id}/cancel`,
      details || {},
    );
    return res.data.data;
  },

  /**
   * Reactivate a subscription that's in 'cancelling' state. Only valid
   * while status === 'cancelling' (i.e. user changed their mind before
   * the cancellation effective date). Restores autoRenew and clears
   * the cancellation fields.
   */
  reactivate: async (id: string): Promise<Subscription> => {
    const res = await api.post<ApiEnvelope<Subscription>>(
      `/subscriptions/${id}/reactivate`,
    );
    return res.data.data;
  },

  /**
   * Renew a subscription that's already 'cancelled' or 'expired'.
   * Re-enables on Paystack and resets the billing window. Throws 400
   * if status is not eligible.
   */
  renew: async (id: string): Promise<Subscription> => {
    const res = await api.post<ApiEnvelope<Subscription>>(
      `/subscriptions/${id}/renew`,
    );
    return res.data.data;
  },

  /**
   * Toggle auto-renewal. Backend PUT /subscriptions/:id is the generic
   * settings-update endpoint; it explicitly rejects planId in the body
   * (use /upgrade for that).
   */
  updateSettings: async (
    id: string,
    updates: { autoRenew?: boolean },
  ): Promise<Subscription> => {
    const res = await api.put<ApiEnvelope<Subscription>>(
      `/subscriptions/${id}`,
      updates,
    );
    return res.data.data;
  },

  /**
   * Update payment method. Currently the backend stores
   * paymentDetails as opaque object; no validation beyond presence.
   */
  updatePaymentMethod: async (
    request: PaymentMethodUpdate,
  ): Promise<Subscription> => {
    const res = await api.put<ApiEnvelope<Subscription>>(
      "/subscriptions/payment-method",
      request,
    );
    return res.data.data;
  },

  /**
   * Get paginated billing history. Backend filters to entries with
   * both lastPaymentDate and paystackLastTransactionReference set.
   */
  getBillingHistory: async (
    page = 1,
    limit = 10,
  ): Promise<BillingHistoryResponse> => {
    const res = await api.get<ApiEnvelope<BillingHistoryResponse>>(
      "/subscriptions/billing-history",
      { params: { page, limit } },
    );
    return res.data.data;
  },

  /**
   * Get usage analytics. Note the URL is /subscriptions/usage (the
   * legacy client called /subscriptions/usage-analytics which 404'd).
   */
  getUsageAnalytics: async (
    startDate?: string,
    endDate?: string,
  ): Promise<UsageAnalytics> => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const res = await api.get<ApiEnvelope<UsageAnalytics>>(
      "/subscriptions/usage",
      { params },
    );
    return res.data.data;
  },
};

export default subscriptionService;
